import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, Trash2, Send, Lock, Bell } from 'lucide-react';
import { API } from '../utils/api';

const CustomerMenuPage = () => {
  const { qrCode } = useParams();
  const [tableNumber, setTableNumber] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [orderNote, setOrderNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isTableOpen, setIsTableOpen] = useState(true);
  const [isTableFull, setIsTableFull] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [deviceId, setDeviceId] = useState('');
  const [menuError, setMenuError] = useState(null);
  const [businessName, setBusinessName] = useState('Cularix');
  const [logo, setLogo] = useState(null);
  const [themeColor, setThemeColor] = useState('#f97316');
  const [businessNameColor, setBusinessNameColor] = useState('#1c1917');
  const [isCallingWaiter, setIsCallingWaiter] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    let storedDeviceId = localStorage.getItem('cularix_device_id');
    if (!storedDeviceId) {
      storedDeviceId = 'dev_' + crypto.randomUUID();
      localStorage.setItem('cularix_device_id', storedDeviceId);
    }
    setDeviceId(storedDeviceId);
    fetchMenu(storedDeviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode]);

  const fetchMenu = async (currentDeviceId) => {
    try {
      const response = await axios.get(`${API}/customer/menu/${qrCode}`);
      setTableNumber(response.data.table_number);
      setCategories(response.data.categories);
      setItems(response.data.items);
      setBusinessName(response.data.business_name || 'Cularix');
      setLogo(response.data.logo || null);
      const safeHex = (val, fallback) => /^#[0-9A-Fa-f]{6}$/.test(val) ? val : fallback;
      setThemeColor(safeHex(response.data.theme_color, '#f97316'));
      setBusinessNameColor(safeHex(response.data.business_name_color, '#1c1917'));
      const open = response.data.is_open;
      setIsTableOpen(open);
      if (open) {
        try {
          const joinRes = await axios.post(`${API}/customer/menu/${qrCode}/join`, { device_id: currentDeviceId });
          setSessionId(joinRes.data.session_id);
        } catch (joinErr) {
          if (joinErr.response && joinErr.response.status === 403) setIsTableFull(true);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 402) {
        setMenuError('Bu restoranın sistem aboneliği geçici olarak durdurulmuştur.');
      } else {
        toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
      }
      setLoading(false);
    }
  };

  const addToCart = (item) => {
    if (!isTableOpen || isTableFull) return;
    const existing = cart.find(c => c.id === item.id);
    if (existing) {
      setCart(cart.map(c => c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
    toast.success(`${item.name} sepete eklendi`);
  };

  const updateQuantity = (itemId, change) => {
    setCart(cart.map(item => {
      if (item.id === itemId) return { ...item, quantity: Math.max(0, item.quantity + change) };
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (itemId) => setCart(cart.filter(item => item.id !== itemId));

  const submitOrder = async () => {
    if (cart.length === 0) { toast.error('Sepetiniz boş'); return; }
    try {
      const orderItems = cart.map(item => ({
        menu_item_id: item.id,
        menu_item_name: item.name,
        quantity: item.quantity,
        price: item.price,
        notes: orderNote,
      }));
      await axios.post(`${API}/orders`, {
        table_number: tableNumber,
        items: orderItems,
        session_id: sessionId,
        device_id: deviceId,
      });
      toast.success('Siparişiniz alındı!');
      setCart([]);
      setOrderNote('');
      setIsCartOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sipariş gönderilemedi');
    }
  };

  const handleCallWaiter = async () => {
    if (!isTableOpen) return;
    setIsCallingWaiter(true);
    try {
      await axios.post(`${API}/customer/menu/${qrCode}/call-waiter`);
      toast.success('Garson çağrıldı, lütfen bekleyin.', { style: { background: themeColor, color: 'white', border: 'none' } });
    } catch {
      toast.error('Garson çağrılamadı, lütfen tekrar deneyin.');
    } finally {
      setTimeout(() => setIsCallingWaiter(false), 5000);
    }
  };

  const filteredItems = selectedCategory === 'all' ? items : items.filter(i => i.category_id === selectedCategory);
  const totalAmount = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const totalItems  = cart.reduce((s, i) => s + i.quantity, 0);

  /* ── Loading ── */
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF8' }}>
      <div className="text-center">
        <div className="w-12 h-12 rounded-full border-4 border-stone-200 border-t-stone-700 animate-spin mx-auto mb-4" />
        <p className="text-stone-500 font-medium">Menü yükleniyor…</p>
      </div>
    </div>
  );

  /* ── Error ── */
  if (menuError) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#FAFAF8' }}>
      <div className="text-center max-w-sm">
        <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
          <Lock size={32} className="text-red-400" />
        </div>
        <h2 className="text-xl font-bold text-stone-800 mb-2">Hizmet Duraklatıldı</h2>
        <p className="text-stone-500 text-sm leading-relaxed">{menuError}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pb-36" style={{ backgroundColor: '#FAFAF8', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── HEADER ── */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, #FFF8F0 0%, #FFF3E6 100%)` }}>
        {/* Decorative blobs */}
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-[0.12]" style={{ background: themeColor }} />
        <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full opacity-[0.08]" style={{ background: themeColor }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-[0.04]" style={{ background: themeColor }} />

        <div className="relative z-10 flex flex-col items-center px-6 pt-12 pb-8">
          {logo && (
            <img src={logo} alt={businessName} className="h-20 w-auto max-w-[180px] object-contain mb-4 drop-shadow-xl" />
          )}
          <h1 className="text-4xl font-medium italic text-center leading-tight"
            style={{ fontFamily: 'Playfair Display, serif', color: businessNameColor }}>
            {businessName}
          </h1>

          <div className="flex items-center gap-2 mt-3 mb-4">
            <div className="h-px w-8 rounded" style={{ backgroundColor: themeColor, opacity: 0.4 }} />
            <span className="text-sm font-semibold tracking-widest uppercase" style={{ color: themeColor }}>Masa {tableNumber}</span>
            <div className="h-px w-8 rounded" style={{ backgroundColor: themeColor, opacity: 0.4 }} />
          </div>

          {isTableOpen && (
            <button
              onClick={handleCallWaiter}
              disabled={isCallingWaiter}
              className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300"
              style={{
                backgroundColor: isCallingWaiter ? '#f5f5f4' : 'white',
                color: isCallingWaiter ? '#a8a29e' : themeColor,
                border: `1.5px solid ${themeColor}40`,
                boxShadow: isCallingWaiter ? 'none' : `0 4px 14px ${themeColor}25`,
              }}
            >
              <Bell size={13} className={isCallingWaiter ? '' : 'animate-pulse'} />
              {isCallingWaiter ? 'Çağrıldı ✓' : 'Garson Çağır'}
            </button>
          )}
        </div>

        {/* Wave */}
        <svg viewBox="0 0 1440 36" fill="#FAFAF8" preserveAspectRatio="none" className="w-full block -mb-px" height="36">
          <path d="M0,36 C360,0 1080,0 1440,36 L1440,36 L0,36 Z" />
        </svg>
      </div>

      {/* ── STATUS BANNER ── */}
      {(!isTableOpen || isTableFull) && (
        <div className="max-w-2xl mx-auto px-4 pt-4">
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
            <Lock size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm text-amber-800">
                {!isTableOpen ? 'Masa Kapalı' : 'Masa Kapasitesi Dolu'}
              </p>
              <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                {!isTableOpen
                  ? 'Sipariş verebilmek için personelden masanızı açmasını isteyin.'
                  : 'Bu masa için belirlenen maksimum cihaz limitine ulaşıldı.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── CATEGORY NAV ── */}
      <div className="sticky top-0 z-20" style={{ background: 'rgba(250,250,248,0.92)', backdropFilter: 'blur(14px)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {[{ id: 'all', name: 'Tümü' }, ...categories].map((cat) => {
              const active = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  data-testid={cat.id === 'all' ? 'category-all' : `category-${cat.id}`}
                  onClick={() => setSelectedCategory(cat.id)}
                  className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200"
                  style={{
                    backgroundColor: active ? themeColor : 'white',
                    color: active ? 'white' : '#78716c',
                    border: `1.5px solid ${active ? themeColor : '#e7e5e4'}`,
                    boxShadow: active ? `0 4px 14px ${themeColor}45` : '0 1px 3px rgba(0,0,0,0.06)',
                  }}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── MENU ITEMS ── */}
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-3">
        {filteredItems.map((item) => (
          <div
            key={item.id}
            data-testid={`menu-item-${item.id}`}
            className="bg-white rounded-2xl overflow-hidden transition-shadow duration-200 hover:shadow-md"
            style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.04)' }}
          >
            <div className="flex items-stretch">
              {/* Content */}
              <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                <div>
                  <h3 className="font-semibold text-stone-900 text-[17px] leading-snug"
                    style={{ fontFamily: 'Playfair Display, serif' }}>
                    {item.name}
                  </h3>
                  {item.description && (
                    <p className="text-stone-500 text-sm leading-relaxed mt-1 line-clamp-2">{item.description}</p>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xl font-bold font-mono" style={{ color: themeColor }}>
                    {item.price} ₺
                  </span>
                  <button
                    data-testid={`add-to-cart-${item.id}`}
                    onClick={() => addToCart(item)}
                    disabled={!isTableOpen || isTableFull}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: (!isTableOpen || isTableFull) ? '#a8a29e' : themeColor,
                      boxShadow: (!isTableOpen || isTableFull) ? 'none' : `0 4px 12px ${themeColor}55`,
                    }}
                  >
                    <Plus size={14} strokeWidth={2.5} />
                    Ekle
                  </button>
                </div>
              </div>

              {/* Image */}
              {item.image_url && (
                <div className="w-28 flex-shrink-0 relative overflow-hidden">
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    style={{ minHeight: '120px' }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        {filteredItems.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
              <ShoppingCart size={22} className="text-stone-400" />
            </div>
            <p className="text-stone-500 font-medium">Bu kategoride ürün bulunamadı</p>
          </div>
        )}
      </div>

      {/* ── BOTTOM CART BAR ── */}
      <div className="fixed bottom-0 left-0 right-0 px-4 pb-6 pt-4"
        style={{ background: 'linear-gradient(to top, #FAFAF8 75%, transparent)' }}>
        <div className="max-w-2xl mx-auto">
          <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="cart-button"
                disabled={!isTableOpen || isTableFull}
                className="w-full flex items-center justify-between px-5 py-4 rounded-2xl font-semibold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: (!isTableOpen || isTableFull) ? '#a8a29e' : themeColor,
                  boxShadow: (!isTableOpen || isTableFull) ? 'none' : `0 8px 28px ${themeColor}65`,
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 rounded-xl w-9 h-9 flex items-center justify-center">
                    <ShoppingCart size={18} />
                  </div>
                  <span className="text-base">
                    {totalItems > 0 ? `${totalItems} ürün` : 'Sepetim'}
                  </span>
                </div>
                <span className="font-mono text-lg font-bold">{totalAmount.toFixed(2)} ₺</span>
              </button>
            </SheetTrigger>

            {/* ── CART SHEET ── */}
            <SheetContent
              className="w-full sm:max-w-md flex flex-col gap-0 p-0 pt-8 [&>button]:text-red-500 [&>button]:opacity-100 [&>button]:hover:text-red-600 [&>button]:hover:bg-red-50 [&>button]:rounded-full [&>button]:w-8 [&>button]:h-8"
              style={{ backgroundColor: '#FAFAF8', borderRadius: '24px 24px 0 0' }}
            >
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-stone-100">
                <SheetTitle className="text-2xl font-medium italic"
                  style={{ fontFamily: 'Playfair Display, serif', color: '#1c1917' }}>
                  Sepetim
                </SheetTitle>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                {cart.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
                      <ShoppingCart size={22} className="text-stone-400" />
                    </div>
                    <p className="text-stone-600 font-medium">Sepetiniz boş</p>
                    <p className="text-stone-400 text-sm mt-1">Menüden ürün ekleyebilirsiniz</p>
                  </div>
                ) : (
                  <>
                    {cart.map((item) => (
                      <div
                        key={item.id}
                        data-testid={`cart-item-${item.id}`}
                        className="bg-white rounded-2xl p-4"
                        style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-stone-900 truncate"
                              style={{ fontFamily: 'Playfair Display, serif' }}>
                              {item.name}
                            </p>
                            <p className="text-sm font-mono font-bold mt-0.5" style={{ color: themeColor }}>
                              {(item.price * item.quantity).toFixed(2)} ₺
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button data-testid={`decrease-${item.id}`} onClick={() => updateQuantity(item.id, -1)}
                              className="w-8 h-8 rounded-full flex items-center justify-center text-stone-600 transition-colors"
                              style={{ backgroundColor: '#f5f5f4', border: '1.5px solid #e7e5e4' }}>
                              <Minus size={13} />
                            </button>
                            <span className="w-6 text-center font-bold text-stone-900 text-sm">{item.quantity}</span>
                            <button data-testid={`increase-${item.id}`} onClick={() => updateQuantity(item.id, 1)}
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-colors"
                              style={{ backgroundColor: themeColor }}>
                              <Plus size={13} />
                            </button>
                            <button data-testid={`remove-${item.id}`} onClick={() => removeFromCart(item.id)}
                              className="w-8 h-8 rounded-full flex items-center justify-center text-red-400 ml-1 transition-colors"
                              style={{ backgroundColor: '#FEF2F2' }}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Note */}
                    <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
                      <label className="block text-sm font-semibold text-stone-600 mb-2">
                        📝 Sipariş Notu <span className="font-normal text-stone-400">(İsteğe bağlı)</span>
                      </label>
                      <textarea
                        value={orderNote}
                        onChange={(e) => setOrderNote(e.target.value)}
                        placeholder="Örn: Az acılı, yanında sos olsun…"
                        rows={3}
                        className="w-full rounded-xl border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-300 resize-none transition"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Submit */}
              {cart.length > 0 && (
                <div className="px-6 pb-8 pt-4 border-t border-stone-100">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-stone-500 font-medium">Toplam</span>
                    <span className="text-2xl font-bold font-mono" style={{ color: themeColor }}>
                      {totalAmount.toFixed(2)} ₺
                    </span>
                  </div>
                  <button
                    data-testid="submit-order-button"
                    onClick={submitOrder}
                    className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-white text-base transition-all duration-200"
                    style={{
                      backgroundColor: themeColor,
                      boxShadow: `0 8px 24px ${themeColor}60`,
                    }}
                  >
                    <Send size={18} />
                    Sipariş Gönder
                  </button>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>

    </div>
  );
};

export default CustomerMenuPage;